import {Model} from 'survey-react-ui';
import DisplayHelpers from '@/helpers/display_helpers';
import {PanelModel, Question, QuestionDropdownModel, SurveyModel} from "survey-core";
import CarecoApi from "@/api/careco-api";
import Icon, {ICONS} from "patient-ping-remedy/packages/icon";
import Button, {StyleType} from "patient-ping-remedy/packages/button";
import IconButton from "patient-ping-remedy/packages/icon-button";
import {Root, createRoot} from "react-dom/client";
import Spacer from "patient-ping-remedy/packages/spacer";
import {sizes} from "patient-ping-remedy/packages/theme";
import {log} from "@/helpers/logger";
import {datePickerWidgetName} from "@common/survey_js/CustomDateWidget";
import {format} from "date-fns";
import {isNull, omitBy} from "lodash-es";
import {FormCodeBinding} from "@/api/dto/forms";
import {DropdownItemType, DropdownItemResponse, User} from "@/api/dto/dto";

type DiagnosisButtonQuestion = Question & {
  __buttonRoot?: Root;
}

const ageGroupMap: Record<string, string> = {
  age_first_use_primary: "age_first_use_primary_input",
  age_first_use_secondary: "age_first_use_secondary_input",
  age_first_use_tertiary: "age_first_use_tertiary_input"
};

const fieldsIgnoredFromAdmissionForm = [
  "mat_drug_was_prescribed_at_prior_admission",
  "mat_drug_prescribed"
];

const editableFieldsAfterSubmit = [
  'mci_number',
  'client|ssn'
];

function addChoices(model: Model, isAdmissionForm: boolean, dropdownItems?: DropdownItemResponse[]) {
  const addChoice = (name: string, choices: string[]) => {
    const question = model.getQuestionByName(name);
    if (question && question.choices) {
      question.choices = choices;
    }
  };

  const treatmentUnitChoices = (dropdownItems ?? []).filter((dropdownItem) =>
    dropdownItem.type === DropdownItemType.DE_TREATMENT_UNIT);

  addChoice('treatment_unit_id', treatmentUnitChoices.map(unit => unit.value));
  addChoice('treatment_unit_name', treatmentUnitChoices.map(unit => unit.label));

  if (!isAdmissionForm) {
    const agencyCodeChoices = model.getQuestionByName('source_agency_code')?.choices;
    addChoice('dest_agency_code_primary', agencyCodeChoices);
    addChoice('dest_agency_code_secondary', agencyCodeChoices);
    addChoice('dest_agency_code_tertiary', agencyCodeChoices);
  }
}

function preserveRemovedValues(model: Model, response: Record<string, unknown>, dropdownItems?: DropdownItemResponse[]) {
  const treatmentUnitChoices = (dropdownItems ?? []).filter((dropdownItem) =>
    dropdownItem.type === DropdownItemType.DE_TREATMENT_UNIT);

  const ensureChoiceExists = (questionName: string, savedValue: unknown) => {
    if (!savedValue || typeof savedValue !== 'string') return;
    const question = model.getQuestionByName(questionName);
    if (!question) return;

    const existingChoices: string[] = (question.choices ?? []).map((c: any) =>
      typeof c === 'string' ? c : c.value ?? c.text
    );

    if (!existingChoices.includes(savedValue)) {
      question.choices = [...(question.choices ?? []), savedValue];
    }
  };

  // Preserve treatment unit values (safety net if backend didn't return them via current_values)
  const savedUnitId = response['treatment_unit_id'];
  const savedUnitName = response['treatment_unit_name'];

  if (savedUnitId && !treatmentUnitChoices.find(u => u.value === savedUnitId)) {
    ensureChoiceExists('treatment_unit_id', savedUnitId);
  }
  if (savedUnitName && !treatmentUnitChoices.find(u => u.label === savedUnitName)) {
    ensureChoiceExists('treatment_unit_name', savedUnitName);
  }

  // Preserve referral code values (from JSON template - if template changed since submission)
  const referralCodeFields = [
    'source_agency_code',
    'dest_agency_code_primary',
    'dest_agency_code_secondary',
    'dest_agency_code_tertiary',
  ];

  referralCodeFields.forEach(fieldName => {
    ensureChoiceExists(fieldName, response[fieldName]);
  });
}

function synchronizeTreatmentUnits(model: Model, dropdownItems?: DropdownItemResponse[]) {
  model.onValueChanged.add((sender, options) => {
    const treatmentUnitChoices = (dropdownItems ?? []).filter((dropdownItem) =>
      dropdownItem.type === DropdownItemType.DE_TREATMENT_UNIT);

    if (options.name === 'treatment_unit_name') {
      const selectedName = options.value;
      const matchedUnit = treatmentUnitChoices.find(unit => unit.label === selectedName);
      const treatmentUnitIdQuestion = sender.getQuestionByName('treatment_unit_id');
      if (treatmentUnitIdQuestion) {
        if (matchedUnit) {
          treatmentUnitIdQuestion.value = matchedUnit.value;
        } else {
          treatmentUnitIdQuestion.value = null;
        }
      }
    }

    if (options.name === 'treatment_unit_id') {
      const selectedId = options.value;
      const matchedUnit = treatmentUnitChoices.find(unit => unit.value === selectedId);
      const treatmentUnitNameQuestion = sender.getQuestionByName('treatment_unit_name');
      if (treatmentUnitNameQuestion) {
        if (matchedUnit) {
          treatmentUnitNameQuestion.value = matchedUnit.label;
        } else {
          treatmentUnitNameQuestion.value = null;
        }
      }
    }
  });
}

function onValueChanged(model: Model, errorsObject: Record<string, boolean>) {
  model.onValueChanged.add((_, options) => {
    const question = options.question;
    if (question) {
      const hasErrors = question.hasErrors(true);
      const isRequiredError = question.errors.some(error => error.text.includes('This field is required'));

      errorsObject[options.name] = hasErrors && !isRequiredError;
      if(question.name === "primary_income_source") {
        handleIncomeSelectionChange(model, options.value);
        return;
      }

      const reverseAgeGroupMap = Object.entries(ageGroupMap).reduce(
        (acc, [k, v]) => ({ ...acc, [v]: k }),
          {} as Record<string, string>
      );

      if (options.name === "substance_type_primary") {
        handleSubstanceTypePrimaryChange(options.value, model);
        return;
      }

      if (ageGroupMap[options.name]) {
        const relatedInput = model.getQuestionByName(ageGroupMap[options.name]);
        const isInputError = relatedInput?.hasErrors(true);
        const isInputRequiredError = relatedInput.errors.some(error => error.text.includes('This field is required'));

        errorsObject[options.name] = relatedInput.visible && isInputError && !isInputRequiredError;
      } else if (reverseAgeGroupMap[options.name]) {
        const relatedGroup = reverseAgeGroupMap[options.name];
        errorsObject[relatedGroup] = hasErrors && !isRequiredError;
      }

      if (options.name === "client|gender") {
        updatePregnancyQuestionBasedOnGender(model, options.value);
      }
    }
  });
}

function onAfterRenderQuestion(model: Model, viewOnly: boolean) {
  model.onAfterRenderQuestion.add((_, options) => {
    if (options.question.name === "substance_type_primary" && !viewOnly) {
      handleSubstanceTypePrimaryChange(options.question.value, model, true);
      return;
    }
  });
}

export function updatePregnancyQuestionBasedOnGender(model: Model, genderValue: string) {
  const pregnancyQuestion = model.getQuestionByName("currently_pregnant");
  if (!pregnancyQuestion) return;

  const gender = genderValue.trim().toLowerCase();

  const allChoices = [
    { text: "Yes", value: "Y" },
    { text: "No", value: "N" },
    { text: "Unknown", value: "U" },
    { text: "Not applicable", value: "NA" }
  ];

  pregnancyQuestion.colCount = 2;

  if (gender === "male") {
    pregnancyQuestion.choices = allChoices;
    if (!pregnancyQuestion.value) {
      pregnancyQuestion.value = "NA";
    }
  } else if (gender === "female") {
    pregnancyQuestion.choices = allChoices.filter(choice => choice.value !== "NA");
    pregnancyQuestion.colCount = 3;
  } else {
    pregnancyQuestion.choices = allChoices;
  }
}

function validateForm(model: Model) {
  model.onValidateQuestion.add((_, options) => {
    if (options.name === 'number_dependent_consumers_income') {
      const value = parseInt(options.value);
      if (value < 1 || value > 20 && value !== 97) {
        options.error = 'Please enter value in range.';
      }
    }

    if (options.name === 'total_custodial_dependent_children') {
      const value = parseInt(options.value);
      if (value < 0 || value > 26 && value !== 97) {
        options.error = 'Please enter value in range.';
      }
    }
    if (options.name === "mci_number" && (!/^\d+$/.test(options.value))) {
      options.error = "Please enter a valid number";
    }

    if (options.name === "discharge_date") {
      const dischargeDate = DisplayHelpers.normalizeDateValue(options.value);
      const admissionDate = DisplayHelpers.normalizeDateValue(model.getValue("event_admission_date"));
      const dsamhAdmissionDate = DisplayHelpers.normalizeDateValue(model.getValue("dsamh_admission_date"));

      if (dischargeDate && admissionDate && dischargeDate < admissionDate) {
        options.error = "Discharge date must be on or after admission date.";
      }

      if (dischargeDate && dsamhAdmissionDate && dischargeDate < dsamhAdmissionDate) {
        options.error = "Discharge Date cannot be older than DSAMH Admit Date";
      }
    }
  });
}

function handleIncomeSelectionChange(model: Model, primarySource: string) {
  const grossIncome = model.getQuestionByName("gross_income");

  const incomeCodeMap: Record<string, number> = {
    "U": 999997,
    "N": 999996
  };

  if (incomeCodeMap[primarySource]) {
    grossIncome.value = incomeCodeMap[primarySource];
    grossIncome.readOnly = true;
  } else {
    if (Object.values(incomeCodeMap).includes(grossIncome.value) && grossIncome.readOnly) {
      grossIncome.value = null;
    }
    grossIncome.readOnly = false;
  }
}

function setDiagnosisChoices(model: Model, carecoApi?: Nullable<CarecoApi>) {
  model.onChoicesLazyLoad.add(async (_, options) => {
    if (carecoApi) {
      if (!options.filter) {
        options.setItems([], 0);
        options.question.choicesLoaded();
        return;
      }

      try {
        const response = await carecoApi.searchDiagnosisCodes(options.filter);

        const choices = response.map((item) => ({
          value: `${item.full_code} - ${item.synonym}`,
          text: `${item.full_code} - ${item.synonym}`
        }));

        options.setItems(choices, choices.length);
        options.question.choicesLoaded();
      } catch (error) {
        log('error', "Failed to load diagnosis options:", error);
        options.setItems([], 0);
        options.question.choicesLoaded();
      }
    }
  });
}

function handleSubstanceTypePrimaryChange(value: string, model: Model, initialUpdate?: boolean) {
  const groups = {
    primary: [
      "administration_route_primary",
      "frequency_use_primary",
      "age_first_use_primary",
      "age_first_use_primary_input",
    ],
    secondary: [
      "substance_type_secondary",
      "administration_route_secondary",
      "frequency_use_secondary",
      "age_first_use_secondary",
      "age_first_use_secondary_input",
    ],
    tertiary: [
      "substance_type_tertiary",
      "administration_route_tertiary",
      "frequency_use_tertiary",
      "age_first_use_tertiary",
      "age_first_use_tertiary_input",
    ],
  };

  function disableAndSetDefaults(names: string[]) {
    names.forEach((name) => {
      const question = model.getQuestionByName(name);
      if (!question) return;

      if (name.includes("frequency_use")) {
        model.setValue(name, "A");
      } else if (name.includes("administration_route") || name.includes("substance_type")) {
        model.setValue(name, "N");
      } else if (name.includes("age_first_use") && !name.includes("input")) {
        model.setValue(name, "96");
      } else if (name.includes("age_first_use") && name.includes("input")) {
        model.setValue(name, null);
        question.visible = false;
      }

      question.readOnly = true;
      if (!name.includes("input")) question.visible = true;
    });
  }

  function enableFields(names: string[]) {
    names.forEach((name) => {
      const question = model.getQuestionByName(name);
      if (!question) return;
      if(!initialUpdate) {
        model.setValue(name, null);
      }
      question.readOnly = false;
    });
  }

  if (value === "N") {
    disableAndSetDefaults(groups.primary);
    disableAndSetDefaults(groups.secondary);
    disableAndSetDefaults(groups.tertiary);
  } else {
    enableFields(groups.primary);
    enableFields(groups.secondary);
    enableFields(groups.tertiary);
  }

  const primarySubstanceQuestion = model.getQuestionByName("substance_type_primary");
  if (primarySubstanceQuestion) {
    primarySubstanceQuestion.readOnly = false;
    primarySubstanceQuestion.visible = true;
  }
}

function handleAddingDiagnosis(model: Model, viewOnly: boolean) {
  model.onAfterRenderQuestion.add((survey, options) => {
    if (options.question.name === "addDiagnosisBtn") {
      const question = options.question as DiagnosisButtonQuestion;
      if (!question.__buttonRoot) {
        question.__buttonRoot = createRoot(options.htmlElement);
      }
      question.__buttonRoot.render(
        <Button styleType={StyleType.TERTIARY} disabled={viewOnly} onClick={() => addNewDiagnosis(survey, options.htmlElement)}>
          <Icon iconClass={ICONS.plus}/> <Spacer itemWidth={sizes.xSmall} /> Add another diagnosis
        </Button>
      );
    }
  });
}

export function addNewDiagnosis(model: Model, button?: HTMLElement, diagnosisNumber: number = 11, viewOnly?: boolean) {
  const panel = model.getPanelByName("diagnosis");
  const buttonIndex = panel.elements.findIndex(element => element.name === "addDiagnosisBtn");

  const newDiagnosisIndex = panel.elements.filter(element =>
    element.name.startsWith("diagnosis_")
  ).length + 1;

  if(newDiagnosisIndex > 11 || diagnosisNumber < newDiagnosisIndex) {
    return;
  }

  const newQuestionName = `diagnosis_${newDiagnosisIndex}`;
  const newQuestion = new QuestionDropdownModel(newQuestionName);

  newQuestion.title = `Diagnosis ${newDiagnosisIndex}`;
  newQuestion.placeholder = "Search...";
  newQuestion.searchEnabled = true;
  newQuestion.choicesLazyLoadEnabled = true;
  newQuestion.readOnly = !!viewOnly;

  panel.elements.splice(buttonIndex, 0, newQuestion);

  if (newDiagnosisIndex > 3) {
    addDeleteDiagnosisButton(model, newQuestionName, panel, viewOnly);
  }

  if (button && newDiagnosisIndex >= 11){
    button.style.display = "none";
  }
  model.render();
}

function addDeleteDiagnosisButton(model: Model, name: string, panel: PanelModel, viewOnly?: boolean) {
  if(viewOnly) {
    return;
  }
  model.onAfterRenderQuestion.add((survey, options) => {
    if (options.question.name === name) {
      const question = options.question as DiagnosisButtonQuestion;
      const row = options.htmlElement.querySelector(".sd-element__content") as HTMLElement;
      row.classList.add("removable-diagnosis-element");

      if (!question.__buttonRoot) {
        const container = document.createElement('div');
        row.appendChild(container);
        question.__buttonRoot = createRoot(container);
      }
      question.__buttonRoot.render(
        <IconButton iconClass={ICONS['trash-alt']} onClick={() => deleteDiagnosis(survey, name, panel)} />
      );
    }
  });
}

function deleteDiagnosis(model: Model, name: string, panel: PanelModel) {
  const lastElement = panel.elements[panel.elements.length - 2];
  panel.removeElement(lastElement);

  const button = document.querySelector<HTMLElement>('[data-name="addDiagnosisBtn"]');
  if (button) {
    button.style.display = "flex";
  }

  const diagnosisNumber = getDiagnosisNumber(name);
  let incrementBy = 0;

  Object.entries(model.getData())
    .filter(([key]) =>  key.startsWith("diagnosis_") && getDiagnosisNumber(key) > diagnosisNumber)
    .sort(([a], [b]) => getDiagnosisNumber(a) - getDiagnosisNumber(b))
    .forEach(([_, value]) => {
      model.setValue(`diagnosis_${diagnosisNumber + incrementBy++}`, value);
    });

  model.clearValue(`diagnosis_${diagnosisNumber + incrementBy}`);
  model.render();

  const question = lastElement as DiagnosisButtonQuestion;
  if (question.__buttonRoot) {
    question.__buttonRoot.unmount();
  }
}

function getDiagnosisNumber(name: string) {
  return parseInt(name.replace("diagnosis_", ""));
}

function setupInitialForm(response: Record<string, unknown>, surveyModel: Model, 
  viewOnly: boolean, codeBinding: FormCodeBinding) {
  Object
    .keys(response)
    .filter(key => key.startsWith("diagnosis_"))
    .forEach((_, index) => {
      if(index > 2 && index < 11) {
        addNewDiagnosis(surveyModel, undefined, index + 1, viewOnly);
      }
    });

  Object.entries(ageGroupMap).forEach(([groupKey, inputKey]) => {
    if (Number.isInteger(response[groupKey])) {
      response[inputKey] = response[groupKey];
      response[groupKey] = "Age at first use in years (1-95)";
    }
  });

  surveyModel
    .getAllQuestions()
    .filter(question => question.customComponent === datePickerWidgetName)
    .forEach((question => {
      const responseValue = response[question.name];
      if (typeof responseValue === "string" && responseValue) { // Ignore ''
        response[question.name] = responseValue.slice(0, 10);
      } else {
        response[question.name] = null;
      }
    }));

  const sourceCode = surveyModel.getQuestionByName("source_agency_code");
  const referralType = surveyModel.getQuestionByName("admission_referral_type");

  if (codeBinding === FormCodeBinding.DSAMH_CRF_DISCHARGE_FORM) {
    if (sourceCode && response["source_agency_code"]) {
      sourceCode.readOnly = true;
    }

    if (referralType && response["admission_referral_type"]) {
      referralType.readOnly = true;
    }
  }
}

export function setupCrfFormSurveyModel(
  model: Model, 
  viewOnly: boolean, 
  editing: boolean,
  codeBound: FormCodeBinding,
  errorsObject: Record<string, boolean>,
  dropdownItems?: DropdownItemResponse[],
  carecoApi?: Nullable<CarecoApi>) {

  const isAdmissionForm = codeBound === FormCodeBinding.DSAMH_CRF_ADMISSION_FORM;

  addChoices(model, isAdmissionForm, dropdownItems);
  synchronizeTreatmentUnits(model, dropdownItems);
  validateForm(model);
  onValueChanged(model, errorsObject);
  onAfterRenderQuestion(model, viewOnly);
  handleAddingDiagnosis(model, viewOnly);
  setDiagnosisChoices(model, carecoApi);

  if (viewOnly) {
    model.getAllQuestions().forEach((question) => {
      question.readOnly = !(editing && editableFieldsAfterSubmit.includes(question.name));
    });
  }
}

async function getAdmissionFormForDischarge(codeBinding: FormCodeBinding, encounterId?: string, carecoApi?: Nullable<CarecoApi>) {
  if(!!encounterId && !!carecoApi && codeBinding === FormCodeBinding.DSAMH_CRF_DISCHARGE_FORM) {
    try {
      const formResult =  await carecoApi.getFormByEncounterAndCodeBinding(
        encounterId,
        FormCodeBinding.DSAMH_CRF_ADMISSION_FORM
      );
      const dischargePrefill = {...formResult};
      fieldsIgnoredFromAdmissionForm.forEach((field) => {
        delete dischargePrefill[field];
      });
      return dischargePrefill;
    } catch (error) {
      log('warn', "Admission form not found or failed to fetch:", error);
    }
  }

  return {};
}

export async function initializeCrfForm(
  surveyModel: SurveyModel, 
  baseResponse: Record<string, unknown> | null,
  viewOnly: boolean,
  codeBinding: FormCodeBinding,
  dropdownItems?: DropdownItemResponse[],
  encounterId?: string, 
  eventType?: string,
  eventDate?: string, 
  currentUser?: User | null, 
  carecoApi?: Nullable<CarecoApi>,
  admitDate?: string) {

  const response = baseResponse ?? await getAdmissionFormForDischarge(codeBinding, encounterId, carecoApi);
  setupInitialForm(response, surveyModel, viewOnly, codeBinding);

  // Preserve removed dropdown values before merging data so they appear in choices
  preserveRemovedValues(surveyModel, response, dropdownItems);

  surveyModel.mergeData(response);

  if (eventType && eventDate) {
    surveyModel.mergeData({
      event_type: eventType,
      // CARECO-6205 (Change A): only the Admission CRF may seed event_admission_date from its own
      // event date. Removing the previous `|| !surveyModel.getValue("event_admission_date")` fallback
      // stops a Discharge CRF from seeding the admission baseline with the ADT discharge event date.
      ...(codeBinding === FormCodeBinding.DSAMH_CRF_ADMISSION_FORM
        ? {event_admission_date: eventDate}
        : {}),
      person_completing_form: DisplayHelpers.formatName(currentUser?.first_name, null, currentUser?.last_name),
    });
  }

  // CARECO-6205 (Change B): give the discharge-date validator (and the visible admission-date field)
  // a real baseline. Prefer a value already sourced from a submitted Admission CRF; otherwise fall back
  // to the encounter's actual ADT admit date threaded in via the f_admit_date search param. This never
  // overwrites a real Admission CRF value and adds no network call.
  if (admitDate && !DisplayHelpers.normalizeDateValue(surveyModel.getValue("event_admission_date"))) {
    surveyModel.mergeData({event_admission_date: admitDate});
  }

  const treatmentUnitId = surveyModel.getValue("treatment_unit_id");
  if (treatmentUnitId) {
    const matchedUnit = dropdownItems?.find(dropdownItem =>
      dropdownItem.type === DropdownItemType.DE_TREATMENT_UNIT && dropdownItem.value === treatmentUnitId);
    if (matchedUnit) {
      surveyModel.setValue("treatment_unit_name", matchedUnit.label);
    } else {
      // Preserve the existing treatment_unit_name from saved data if the unit was removed from the list
      const savedName = response["treatment_unit_name"];
      if (savedName) {
        surveyModel.setValue("treatment_unit_name", savedName);
      }
    }
  }
}

export function postRenderingCrfForm(surveyModel: SurveyModel) {
  const diagnosisCount = Object
    .keys(surveyModel.getData())
    .filter((key) => key.startsWith("diagnosis_"))
    .length;
  if(diagnosisCount >= 11) {
    const button = document.querySelector<HTMLElement>('[data-name="addDiagnosisBtn"]');
    if(button) {
      button.style.display = "none";
    }
  }
}

export function formatCrfDataToSubmit(surveyModel: SurveyModel) {
  const response = {...surveyModel?.getData()};
  Object
    .entries(response)
    .filter(([key, _]) => key.startsWith("diagnosis_"))
    .sort(([a], [b]) => getDiagnosisNumber(a) - getDiagnosisNumber(b))
    .forEach(([key, value], index) => {
      delete response[key];
      response[`diagnosis_${index + 1}`] = value;
    });

  Object.entries(ageGroupMap).forEach(([groupKey, inputKey]) => {
    if (response[groupKey] === "Age at first use in years (1-95)") {
      const inputQuestion = surveyModel?.getQuestionByName(inputKey);
      response[groupKey] = inputQuestion?.value ?? response[groupKey];
    }
    delete response[inputKey];
  });

  surveyModel?.getAllQuestions()
    .filter(question => question.customComponent === datePickerWidgetName)
    .forEach((question => {
      if(question.name && response[question.name]) {
        response[question.name] = response[question.name] instanceof Date ?
          format(response[question.name], 'yyyy-MM-dd') : response[question.name].slice(0, 10);
      }
    }));

  return JSON.stringify(omitBy(response, isNull));
}
